import { useState } from "react";
import { youtubeEmbedUrl, youtubeThumbnail } from "../lib/youtube";

/**
 * VideoGallery — grid of YouTube thumbnails; clicking one swaps a featured player.
 * Props:
 *   - videos: string[] of YouTube video IDs
 *   - title?: string  (label above the gallery)
 *   - testIdPrefix?: string
 */
export default function VideoGallery({ videos = [], title = "/ Videos", testIdPrefix = "video-gallery" }) {
    const ids = (videos || []).filter(Boolean);
    const [active, setActive] = useState(ids[0] || "");
    if (ids.length === 0) return null;

    return (
        <section data-testid={testIdPrefix} className="mt-12">
            <div className="label-mono mb-4">{title}</div>

            <div className="rounded-sm border border-avx-line overflow-hidden bg-black">
                <div className="aspect-video w-full">
                    <iframe
                        key={active}
                        src={youtubeEmbedUrl(active)}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                        data-testid={`${testIdPrefix}-active-iframe`}
                    />
                </div>
            </div>

            {ids.length > 1 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid={`${testIdPrefix}-thumbs`}>
                    {ids.map((id) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setActive(id)}
                            data-testid={`${testIdPrefix}-thumb-${id}`}
                            className={`group relative aspect-video overflow-hidden rounded-sm border transition-all ${
                                id === active ? "border-avx-blue shadow-[0_0_18px_rgba(63,169,255,0.35)]" : "border-avx-line hover:border-avx-blue/60"
                            }`}
                        >
                            <img
                                src={youtubeThumbnail(id)}
                                alt="Video preview"
                                loading="lazy"
                                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition-colors">
                                <div className="w-10 h-10 rounded-full bg-avx-blue/90 flex items-center justify-center">
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-avx-bg ml-0.5">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
}
